import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import moment from 'moment';
import DayColumn from './DayColumn';
import { colors } from 'kangarootime-ui';
import AddCircleOutline from '../../../../../assets/svg/addCircleOutline';
import { capitalize } from '../../../../../utils/string';
import useWindowSize from '../../../../../hooks/useWindowResize';
import ShiftActionsPopover from './ShiftActionsPopover';
import { sortBy } from 'lodash';
import './WeekView.css';

/**
 * shifts can only be created if:
 * - a schedule for the current week has been created
 * - schedule is in DRAFT state
 */
const WeekView = ({
  shiftsGroupedByDay,
  roomsById,
  employeesById,
  minutesWorkedForWeekByEmployee,
  projectedRoomAttendance,
  roomRatios,
  datesInVisibleWeek,
  singleDayView,
  createNewShift,
  editShift,
  copyShift,
  deleteShift,
  showCreateButton
}) => {
  const [shiftActionsPopoverAnchor, setShiftActionsPopoverAnchor] = useState(null);
  const [selectedCell, setSelectedCell] = useState(null);
  const windowSize = useWindowSize();

  const formatTime = date => moment(date).format('h:mm a');
  const employeeName = id => employeesById[id] ? employeesById[id].name : '';
  const employeePhoto = id => employeesById[id] ? employeesById[id].image : '';
  let numberOfFillerColumnsPerRoom = {};

  /**
   * find the largest number of shifts scheduled in a room for an entire week
   * will be used to determine the number of filler rows for each room
   * Example:
   * Room A has 1 shift scheduled on Monday, Room B has 2 shifts scheduled on Tuesday, and Room C has 3 shifts scheduled on Thursday
   * - Room A requires 2 additional rows and Room B requires 1 additional row so that everything remains lined up
   */
  // use if every room should have the same number of rows
  // const minimumNumberOfCells = Object.keys(shiftsGroupedByDay).map(dayName => {
  //   // calculate the max number of shifts scheduled for a day
  //   const roomsObject = shiftsGroupedByDay[dayName];

  //   return Object.values(roomsObject).map(roomShifts => roomShifts.length).reduce((a, b) => Math.max(a, b), 0);
  // }).reduce((a, b) => Math.max(a, b), 0);

  // get the largest numbe rof shifts scheduled per room in the week so that rows remain equal
  Object.keys(shiftsGroupedByDay).forEach(dayName => {
    // calculate the max number of shifts scheduled for a day
    const roomsObject = shiftsGroupedByDay[dayName];

    Object.keys(roomsObject).forEach(roomId => {
      numberOfFillerColumnsPerRoom[roomId] = numberOfFillerColumnsPerRoom[roomId] === undefined ? roomsObject[roomId].length : Math.max(numberOfFillerColumnsPerRoom[roomId], roomsObject[roomId].length);
    });
  });

  const containerClassnames = classnames({
    'week-view-container': true,
    'week-view-container-single': singleDayView
  });

  return (
    <div className={containerClassnames}>
      { Object.keys(shiftsGroupedByDay).map((dayName, index) => {
        const dayDate = moment(datesInVisibleWeek[index]);
        const shiftsForDay = shiftsGroupedByDay[dayName];
        const isFirstColumn = index === 0;
        const canCreateShiftForDay = moment(dayDate).isSameOrAfter(moment(), 'date');

        /**
         * create an object where the key is the room id and the value is its ratio.
         * then sort in ascending order so lower ratios (1:4) appear at the top of the table
         * if a room id doesn't have a ratio (ex: Unassigned aka Floats), it will appear at the top of the table
         * `sorter` will then be an array of strings where each string represents a room id
         */
        let sorter = { ...shiftsForDay };
        Object.keys(sorter).forEach(roomId => { sorter[roomId] = roomRatios[roomId] || -1; });
        sorter = Object.keys(sorter).sort((a, b) => sorter[a] - sorter[b]);

        return (
          <DayColumn.Container key={`room-view-table-column-container-${dayName}`}>
            <DayColumn.Header label={capitalize(dayName)} sublabel={dayDate.format('MMM D')} />
            { sorter.map((roomId, index) => {
              const roomName = parseInt(roomId, 10) === 0 ? 'Floats' : roomsById[roomId];
              // sort by the shift's start time in to ensure they are in ascending order
              const shiftsForRoom = sortBy(shiftsForDay[roomId], shift => shift.start);
              // only create filler cells if there are shifts created in the room for the visible week; the 'create shift' cell will be the only cell if there are none
              const fillerAmount = shiftsForRoom.length === 0 && numberOfFillerColumnsPerRoom[roomId] === 0 ? 0 : numberOfFillerColumnsPerRoom[roomId] - shiftsForRoom.length;
              const fillerRows = [...Array(fillerAmount)].map((x, index) => <DayColumn.EmployeeCell key={`filler-row-${index}`} />);

              return (
                <div key={`room-view-table-column-${roomId}`}>
                  <DayColumn.RoomLabel
                    name={roomsById[roomId] ? roomsById[roomId] : 'Floats'}
                    projectedAttendenceAM={projectedRoomAttendance[roomId] && projectedRoomAttendance[roomId][dayName] && projectedRoomAttendance[roomId][dayName]['am']}
                    projectedAttendencePM={projectedRoomAttendance[roomId] && projectedRoomAttendance[roomId][dayName] && projectedRoomAttendance[roomId][dayName]['pm']}
                    showInitials={isFirstColumn || windowSize.innerWidth <= 768}
                    ratio={roomRatios[roomId] && roomRatios[roomId]}
                  />
                  <div className={singleDayView ? 'flex-rows' : 'flex-columns'}>
                    { shiftsForRoom.map((employeeShift, index) => {
                      const timeframe = `${formatTime(employeeShift.start)} - ${formatTime(employeeShift.end)}`;
                      const shiftDurationInMinutes = (moment(employeeShift.end).diff(moment(employeeShift.start), 'minutes') - employeeShift.breakMinutes);
                      const shiftDurationInHours = (shiftDurationInMinutes / 60).toFixed(2);
                      // need to include the value of `showCreateButton` in the cell's key so the CRUD actions would show up when a schedule goes from published -> draft or vice versa
                      const cellKey = `room-view-table-employee-cell-${employeeShift.id}-${showCreateButton}-${employeeShift.removeShiftId || ''}`;

                      return (
                        <DayColumn.EmployeeCell
                          key={cellKey}
                          name={employeeName(employeeShift.employeeId)}
                          photo={employeePhoto(employeeShift.employeeId)}
                          timeframe={timeframe}
                          shiftDuration={shiftDurationInHours}
                          weeklyHours={parseFloat(minutesWorkedForWeekByEmployee[employeeShift.employeeId] / 60).toFixed(2)}
                          selectShift={event => {
                            // shouldn't be able to select a shift we plan to delete
                            if (showCreateButton && !employeeShift.removeShiftId) {
                              setShiftActionsPopoverAnchor(event.currentTarget);
                              setSelectedCell({
                                employee: {},
                                shift: employeeShift,
                                room: { id: roomId, name: roomName }
                              });
                            }
                          }}
                          isSelected={selectedCell && selectedCell.shift.id === employeeShift.id}
                          isDraft={/__DRAFT/.test(employeeShift.id)}
                          isToBeRemoved={/__REMOVE/.test(employeeShift.removeShiftId)} // this property is added to the shift object when we want to remove a shift
                        />
                      );
                    })}
                    { fillerRows }
                    <div
                      onClick={() => canCreateShiftForDay && showCreateButton && createNewShift({}, dayDate, { id: roomId, name: roomName })}
                      className='flex'
                      style={{ justifyContent: 'center', alignItems: 'center', height: 48, cursor: canCreateShiftForDay ? 'pointer' : 'not-allowed', padding: '0 8px' }}
                    >
                      { showCreateButton &&
                        <AddCircleOutline fill={canCreateShiftForDay ? colors.brand_3 : colors.athensGray} />
                      }
                    </div>
                  </div>
                </div>
              );
            })}
            { selectedCell &&
              <ShiftActionsPopover
                anchorEl={shiftActionsPopoverAnchor}
                canPerformEdit={moment(selectedCell.shift.start).isAfter(moment())}
                onClose={() => {
                  setShiftActionsPopoverAnchor(null);
                  setSelectedCell(null);
                }}
                onEdit={() => {
                  setShiftActionsPopoverAnchor(null);
                  editShift({}, selectedCell.shift, selectedCell.room);
                }}
                onCopy={() => {
                  setShiftActionsPopoverAnchor(null);
                  copyShift(selectedCell.shift);
                }}
                onDelete={() => {
                  setShiftActionsPopoverAnchor(null);
                  deleteShift(selectedCell.shift);
                }}
              />
            }
          </DayColumn.Container>
        );
      })}
    </div>
  );
};

WeekView.propTypes = {
  shiftsGroupedByDay: PropTypes.shape({
    monday: PropTypes.shape({
      [PropTypes.number]: PropTypes.arrayOf(PropTypes.object)
    }),
    tuesday: PropTypes.shape({
      [PropTypes.number]: PropTypes.arrayOf(PropTypes.object)
    }),
    wednesday: PropTypes.shape({
      [PropTypes.number]: PropTypes.arrayOf(PropTypes.object)
    }),
    thursday: PropTypes.shape({
      [PropTypes.number]: PropTypes.arrayOf(PropTypes.object)
    }),
    friday: PropTypes.shape({
      [PropTypes.number]: PropTypes.arrayOf(PropTypes.object)
    })
  }),
  roomsById: PropTypes.shape({
    [PropTypes.number]: PropTypes.string
  }),
  employeesById: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    image: PropTypes.string,
    status: PropTypes.string
  }),
  projectedRoomAttendance: PropTypes.shape({
    [PropTypes.number]: PropTypes.shape({
      monday: PropTypes.shape({
        am: PropTypes.number,
        pm: PropTypes.number
      }),
      tuesday: PropTypes.shape({
        am: PropTypes.number,
        pm: PropTypes.number
      }),
      wednesday: PropTypes.shape({
        am: PropTypes.number,
        pm: PropTypes.number
      }),
      thursday: PropTypes.shape({
        am: PropTypes.number,
        pm: PropTypes.number
      }),
      friday: PropTypes.shape({
        am: PropTypes.number,
        pm: PropTypes.number
      }),
      saturday: PropTypes.shape({
        am: PropTypes.number,
        pm: PropTypes.number
      }),
      sunday: PropTypes.shape({
        am: PropTypes.number,
        pm: PropTypes.number
      })
    })
  }),
  roomRatios: PropTypes.shape({
    [PropTypes.number]: PropTypes.number
  }),
  datesInVisibleWeek: PropTypes.arrayOf(PropTypes.instanceOf(Date)),
  singleDayView: PropTypes.bool,
  createNewShift: PropTypes.func,
  editShift: PropTypes.func,
  copyShift: PropTypes.func,
  deleteShift: PropTypes.func,
  minutesWorkedForWeekByEmployee: PropTypes.shape({
    [PropTypes.number]: PropTypes.number
  })
};

export default WeekView;
